'use client';
import React from 'react';
import AppLayout from '@/components/AppLayout';
import { Truck, Info, Building2, Package, Activity } from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


const supplyChainEvents = [
  { id: 'sc-001', event: 'Chile Copper Mine Strike', category: 'Supplier Disruption', severity: 'Medium', region: 'Chile / S. America', affectedSuppliers: 'Codelco Andina (Tier-2)', acmeImpact: '3 procurement contracts affected — copper components', estimatedDelay: '2–3 weeks', action: 'Review buffer stock. Identify alternative copper suppliers.' },
  { id: 'sc-002', event: 'Red Sea Rerouting — Logistics Cascade', category: 'Logistics Disruption', severity: 'High', region: 'Red Sea / Global', affectedSuppliers: 'Multiple freight carriers', acmeImpact: '1 active shipment rerouted — +12 day delay', estimatedDelay: '12–14 days', action: 'Notify customers. Review insurance. Update delivery windows.' },
  { id: 'sc-003', event: 'Taiwan Strait — Electronics Sourcing Risk', category: 'Procurement Risk', severity: 'High', region: 'Taiwan / East Asia', affectedSuppliers: '3 Acme Corp Taiwan-based component suppliers', acmeImpact: 'Q3 electronics component procurement at risk', estimatedDelay: 'Potential 30+ days if closure', action: 'Assess alternative sourcing. Build buffer stock for critical components.' },
  { id: 'sc-004', event: 'Pakistan Grid Instability', category: 'Manufacturing Risk', severity: 'Medium', region: 'Pakistan / S. Asia', affectedSuppliers: 'Textile and manufacturing suppliers', acmeImpact: 'Low direct exposure — Tier-3 textile suppliers', estimatedDelay: '1–2 weeks', action: 'Monitor — no immediate action required.' },
];

const severityColor: Record<string, string> = {
  Critical: 'text-danger bg-danger/10',
  High: 'text-warning bg-warning/10',
  Medium: 'text-info bg-info/10',
};

export default function SupplyChainPage() {
  return (
    <AppLayout activeRoute="/supply-chain">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Supply Chain & Operational Risk</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Supplier disruptions, logistics risk, and procurement exposure</p>
          </div>
          <Link href="/company-analyser" className="flex items-center gap-2 text-sm bg-primary text-primary-foreground font-medium rounded-md px-4 py-2 hover:opacity-90 transition-all">
            Company Analyser
          </Link>
        </div>

        {/* How teams use this */}
        <div className="card-elevated rounded-xl border border-warning/20 bg-warning/5 p-4">
          <div className="flex items-start gap-3">
            <Info size={15} className="text-warning flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-warning uppercase tracking-wide mb-1">How teams use this</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Procurement and operations teams use supply-chain disruption data to identify second-order supplier exposure, trigger alternative sourcing workflows, update delivery commitments, and quantify the operational and financial impact of geopolitical events on their supplier network before shortages materialise.
              </p>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Supply Chain Score', value: '67', unit: '/ 100', icon: Activity, color: 'text-warning', sub: 'Elevated — 4 active disruptions' },
            { label: 'Disrupted Routes', value: '19', unit: 'routes', icon: Truck, color: 'text-danger', sub: 'Red Sea, Taiwan Strait' },
            { label: 'Affected Suppliers', value: '6', unit: 'entities', icon: Package, color: 'text-warning', sub: '3 Acme Corp Tier-1/2 suppliers' },
            { label: 'Acme Corp Exposure', value: 'High', unit: '', icon: Building2, color: 'text-warning', sub: '3 active procurement impacts' },
          ].map(({ label, value, unit, icon: Icon, color, sub }) => (
            <div key={label} className="card-elevated rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className={color} />
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <p className={`text-2xl font-bold font-tabular ${color}`}>{value}</p>
                {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
              </div>
              <p className="text-2xs text-muted-foreground">{sub}</p>
            </div>
          ))}
        </div>

        {/* Acme Corp Supply Chain Exposure */}
        <div className="card-elevated rounded-xl border border-warning/20 bg-warning/5 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Building2 size={14} className="text-warning" />
            <p className="text-sm font-semibold text-foreground">Acme Corp — Supply Chain Exposure Summary</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: 'Affected Suppliers', value: '3', sub: 'Tier-1 and Tier-2 disruptions', color: 'text-danger' },
              { label: 'Affected Jurisdictions', value: '4', sub: 'Chile, Taiwan, Red Sea, Pakistan', color: 'text-warning' },
              { label: 'Estimated Impact', value: 'Medium', sub: '2–3 week delay on copper lines', color: 'text-warning' },
            ].map(({ label, value, sub, color }) => (
              <div key={label} className="bg-card/50 rounded-lg p-3 border border-border">
                <p className="text-2xs text-muted-foreground mb-1">{label}</p>
                <p className={`text-xl font-bold font-tabular ${color} mb-0.5`}>{value}</p>
                <p className="text-2xs text-muted-foreground">{sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Events */}
        <div className="card-elevated rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <p className="text-sm font-semibold text-foreground">Active Supply Chain Disruptions</p>
          </div>
          <div className="divide-y divide-border">
            {supplyChainEvents.map((event) => (
              <div key={event.id} className="p-4 hover:bg-muted/20 transition-colors">
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Truck size={14} className="text-primary flex-shrink-0" />
                    <p className="text-sm font-semibold text-foreground">{event.event}</p>
                    <span className={`text-2xs px-1.5 py-0.5 rounded font-semibold ${severityColor[event.severity]}`}>{event.severity}</span>
                  </div>
                  <span className="text-2xs text-muted-foreground">{event.region}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{event.category}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                  <div>
                    <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Affected Suppliers</p>
                    <p className="text-xs text-foreground">{event.affectedSuppliers}</p>
                  </div>
                  <div>
                    <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Acme Corp Impact</p>
                    <p className="text-xs text-foreground">{event.acmeImpact}</p>
                  </div>
                  <div>
                    <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Estimated Delay</p>
                    <p className="text-xs text-warning font-medium">{event.estimatedDelay}</p>
                  </div>
                  <div>
                    <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Recommended Action</p>
                    <p className="text-xs text-foreground">{event.action}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
